import {
  showSuccess,
  showConfirm,
  showCustom,
  showLoading,
  hideLoading,
} from "@/assets/js/utils/alertService";
import Swal from "sweetalert2";
import api from "@/assets/js/utils/api.js";
import * as XLSX from "xlsx"; // ← Excel 產生
import { saveAs } from "file-saver"; // ← 下載檔案

export default {
  name: "OrderManagement",

  data() {
    return {
      searchQuery: "",
      activeFilter: "全部訂單",
      showDetailModal: false,
      showAddOrderModal: false,
      selectedOrder: null,
      activeDropdown: null,
      activePaymentDropdown: null,
      activeStatusDropdown: null,

      filters: [
        { label: "全部訂單", value: "全部訂單" },
        { label: "待處理", value: "待處理" },
        { label: "處理中", value: "處理中" },
        { label: "已出貨", value: "已出貨" },
        { label: "已完成", value: "已完成" },
        { label: "已取消", value: "已取消" }, // 如果你有用到
        { label: "申請退貨", value: "申請退貨" },
      ],
      orders: [], // 先空陣列，改用 fetchOrders 從後端抓取

      currentPage: 1, // ✅ 當前頁數
      itemsPerPage: 5, // ✅ 每頁顯示筆數
    };
  },

  watch: {
    currentPage() {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
  },

  computed: {
    // ✅ 訂單統計
    stats() {
      return {
        pending: this.orders.filter((o) => o.status === "待處理").length,
        processing: this.orders.filter((o) => o.status === "處理中").length,
        shipped: this.orders.filter((o) => o.status === "已出貨").length,
        completed: this.orders.filter((o) => o.status === "已完成").length,
        cancelled: this.orders.filter((o) => o.status === "已取消").length,
      };
    },

    // ✅ 搜尋 + 篩選
    filteredOrders() {
      let result = this.orders;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(
          (order) =>
            order.orderNumber.toLowerCase().includes(query) ||
            order.customerName.toLowerCase().includes(query) ||
            order.contactPerson.toLowerCase().includes(query) ||
            order.phone.includes(query),
        );
      }

      if (this.activeFilter !== "全部訂單") {
        result = result.filter((order) => order.status === this.activeFilter);
      }
      return result;
    },

    // ✅ 分頁後的訂單
    paginatedOrders() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredOrders.slice(start, end);
    },

    // ✅ 總頁數
    totalPages() {
      return Math.ceil(this.filteredOrders.length / this.itemsPerPage);
    },
  },

  mounted() {
    this.fetchOrders().then(() => {
      // 讀取網址 ?id=21
      const orderId = this.$route.query.id;

      if (orderId) {
        // 找到該訂單
        const target = this.orders.find((o) => o.id == orderId);

        if (target) {
          // 自動開啟詳細資料
          this.viewOrderDetail(target);
        }
      }
    });

    document.addEventListener("click", () => {
      this.activeDropdown = null; // ← 註解：點外面關閉下拉
      this.activePaymentDropdown = null; // ⭐ 新增
      this.activeStatusDropdown = null; // ⭐ 新增
    });
  },

  methods: {
    async fetchOrders() {
      try {
        const response = await api.get("/order/list"); // ← 改成 axios 公用方法

        const result = response.data; // ← axios 回傳格式不一樣

        if (!result.success) {
          showCustom({
            icon: "error",
            title: "讀取失敗",
            text: result.message ?? "後端回傳錯誤",
          });
          return;
        }

        this.orders = result.data.map((o) => ({
          id: o.Id, // 訂單ID
          orderNumber: o.OrderNo, // 訂單編號
          customerName: o.CompanyName, // 客戶名稱
          contactPerson: o.CustomerName, // 你後端沒有 → 暫時用客戶名稱
          phone: o.PhoneNumber, // 電話
          email: o.Email, // 信箱（你前端目前沒用到）
          address: o.Address, // 暫時補假資料，避免 UI 爆掉
          productCount: o.ProductCount, // ⭐⭐【新增這行】商品項目筆數
          status: o.OrderStatus, // 後端中文 → 前端英文
          paymentMethod: o.PaymentMethod ?? "",
          paymentStatus: o.PaymentStatus ? "paid" : "unpaid",
          orderDate: o.CreatedDate.split("T")[0], // yyyy-MM-dd
          shippedDate: o.ShippedDate ? o.ShippedDate.split("T")[0] : null, // 出貨日期
          totalAmount: o.TotalAmount, // 總金額
          itemCount: o.ProductCount, // ⭐⭐【新增這行】商品項目筆數
          items: [], // ⭐⭐ 保持空集合（詳細從 detail API 拿）
        }));
        this.updateFilterCounts(); // ← 加上這行
      } catch (error) {
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: "無法取得訂單列表，請稍後重試。",
        });
      }
    },

    handleSearch() {
      // 搜尋邏輯由 computed 處理
    },

    updateFilterCounts() {
      this.filters = this.filters.map((f) => {
        if (f.value === "全部訂單") {
          return { ...f, count: this.orders.length };
        }
        return {
          ...f,
          count: this.orders.filter((o) => o.status === f.value).length,
        };
      });
    },

    getPaymentClass(paymentStatus) {
      const classes = {
        unpaid: "payment-unpaid",
        paid: "payment-paid",
        partial: "payment-partial",
      };
      return classes[paymentStatus] || "";
    },

    getPaymentText(paymentStatus) {
      const texts = {
        unpaid: "未付款",
        paid: "已付款",
        partial: "部分付款",
      };
      return texts[paymentStatus] || paymentStatus;
    },

    getItemsPreview(order) {
      return `共有 ${order.itemCount} 項商品`;
    },

    async viewOrderDetail(order) {
      try {
        showLoading("正在取得訂單詳細...");

        const response = await api.get(`/order/detail/${order.id}`);
        const result = response.data;

        if (!result.success) {
          hideLoading();
          showCustom({
            icon: "error",
            title: "查詢失敗",
            text: result.message ?? "後端錯誤",
          });
          return;
        }

        const d = result.data;

        // ⭐⭐⭐ 重新 mapping，確保前端欄位完整 ⭐⭐⭐
        this.selectedOrder = {
          id: d.Id,
          orderNumber: d.OrderNo,
          status: d.OrderStatus,
          paymentStatus: d.PaymentStatus ? "paid" : "unpaid",
          orderDate: d.CreatedDate?.split("T")[0] ?? "",
          shippedDate: d.ShippedDate ? d.ShippedDate.split("T")[0] : null,
          note: d.Note ?? "",
          totalAmount: d.TotalAmount ?? 0,
          paymentMethod: d.PaymentMethod ?? "",
          // 客戶資訊
          customerName: d.Customer?.CompanyName ?? "",
          contactPerson: d.Customer?.FullName ?? "",
          phone: d.Customer?.PhoneNumber ?? "",
          address: d.Address ?? "",

          // 商品明細
          items: (d.Items ?? []).map((x) => {
            // 組商品名稱 (規格)
            let name = x.ProductName;
            if (x.VariantName) {
              name = `${x.ProductName} (${x.VariantName})`; // 例如： 動物用胃管 (8Fr)
            }

            // 若為贈品 → 額外標示
            if (x.Price === 0) {
              name += ` (贈品)`;
            }

            return {
              id: x.Id,
              name: name,
              price: x.Price,
              quantity: x.Quantity,
              subTotal: x.SubTotal,
              productId: x.ProductId, // 建議保留
              variantId: x.VariantId ?? null, // 建議保留
            };
          }),
        };

        this.showDetailModal = true;
        hideLoading();
      } catch (error) {
        hideLoading();
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: "無法取得訂單詳細資訊",
        });
      }
    },
    editOrder(order) {
      showCustom({
        icon: "info",
        title: "功能開發中",
        text: `即將開放編輯訂單「${order.orderNumber}」`,
      });
    },

    // ==============================================
    // ⭐ 修正版 PDF 下載方法 - 移除 about:blank
    // ==============================================
    // ==============================================
    // ⭐ 真正PDF生成方案 - 使用瀏覽器原生打印API
    // ==============================================
    async generatePDF(order) {
      showLoading("正在生成出貨單PDF...");

      setTimeout(() => {
        hideLoading();
        showSuccess(`出貨單「${order.orderNumber}」已生成!`, "即將開始下載");
        this.downloadRealPDF(order);
      }, 1500);
    },

    downloadRealPDF(order) {
      // 建立一個完全隱藏的新視窗
      const printWindow = window.open(
        "",
        "_blank",
        "width=800,height=600,scrollbars=yes,resizable=yes",
      );

      if (!printWindow) {
        showCustom({
          icon: "error",
          title: "彈窗被阻擋",
          text: "請允許彈窗並重新嘗試",
        });
        return;
      }

      // 設定視窗標題（這會取代 about:blank）
      printWindow.document.title = `出貨單-${order.orderNumber}`;

      // 寫入優化的 HTML 內容（移除 about:blank 顯示）
      const optimizedHTML = this.generateOptimizedPDFContent(order);
      printWindow.document.write(optimizedHTML);
      printWindow.document.close();

      // 等待載入完成
      printWindow.addEventListener("load", () => {
        setTimeout(() => {
          // 使用現代的列印API
          if (printWindow.navigator.userAgent.includes("Chrome")) {
            // Chrome 可以直接另存為PDF
            printWindow.print();

            // 提示用戶選擇「另存為PDF」
            showCustom({
              icon: "info",
              title: "列印提示",
              text: "在列印對話框中，請選擇「另存為PDF」作為印表機選項",
              timer: 5000,
            });
          } else {
            // 其他瀏覽器
            printWindow.print();
          }

          // 列印對話框關閉後自動關閉視窗
          printWindow.addEventListener("afterprint", () => {
            setTimeout(() => printWindow.close(), 1000);
          });
        }, 1000);
      });
    },

    // 優化的PDF內容生成（移除about:blank顯示）
    generateOptimizedPDFContent(order) {
      // 使用原有的 generatePDFContent，但加入額外的CSS來隱藏about:blank
      const originalHTML = this.generatePDFContent(order);

      // 在CSS中加入隱藏網址的樣式
      const optimizedCSS = `
        /* 隱藏列印時的網址顯示 */
        @media print {
          @page {
            margin: 0.5in;
            size: A4;
            /* 移除頁首頁尾 */
            margin-top: 0.5in;
            margin-bottom: 0.5in;
          }
          
          /* 隱藏可能的網址顯示 */
          body::before,
          body::after,
          html::before,
          html::after {
            display: none !important;
          }
          
          /* 確保沒有背景 */
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
        
        /* 隱藏視窗中的網址列等元素 */
        @media screen {
          body {
            margin: 0;
            padding: 0;
          }
        }
      `;

      // 將新的CSS插入到HTML中
      return originalHTML.replace("</style>", optimizedCSS + "</style>");
    },

    downloadPDF(order) {
      // 方法1：使用 Blob 和下載連結（推薦）
      try {
        const htmlContent = this.generatePDFContent(order);
        const blob = new Blob([htmlContent], { type: "text/html" });
        const url = URL.createObjectURL(blob);

        // 建立下載連結
        const downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.download = `出貨單-${order.orderNumber}-${new Date()
          .toISOString()
          .slice(0, 10)}.html`;
        downloadLink.style.display = "none";

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // 清除 URL
        setTimeout(() => URL.revokeObjectURL(url), 1000);

        // 提示用戶可以開啟檔案進行列印
        showCustom({
          icon: "info",
          title: "檔案已下載",
          text: "HTML檔案已下載到您的電腦，請開啟檔案後使用瀏覽器列印功能",
        });
      } catch (error) {
        console.error("下載失敗，改用列印方式:", error);
        this.printPDF(order); // 如果下載失敗，改用列印
      }
    },

    // 備用的列印方法（移除 about:blank）
    printPDF(order) {
      const htmlContent = this.generatePDFContent(order);

      // 建立隱藏的 iframe 進行列印
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.top = "-10000px";
      iframe.style.left = "-10000px";
      iframe.style.width = "0px";
      iframe.style.height = "0px";

      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();

      // 等待內容載入完成後列印
      iframe.onload = function () {
        setTimeout(() => {
          iframe.contentWindow.print();

          // 列印完成後移除 iframe
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        }, 500);
      };
    },

    // ==============================================
    // ⭐ 修正版PDF生成器 - 確保商品資料正確顯示
    // ==============================================
    generatePDFContent(order) {
      // 檢查並修正商品資料
      if (!order.items || order.items.length === 0) {
        console.error(
          "警告：商品明細為空，請檢查 viewOrderDetail 是否正確載入資料",
        );

        // 顯示警告訊息而不是假資料
        return this.generateErrorPDF(
          order,
          "商品明細資料載入失敗，請重新查看訂單詳情後再生成PDF",
        );
      }

      // 生成商品表格HTML
      const itemsHTML = order.items
        .map((item, index) => {
          // 確保每個欄位都有預設值
          const itemName = item.name || item.ProductName || "商品名稱缺失";
          const quantity = Number(item.quantity) || Number(item.Quantity) || 0;
          const price = Number(item.price) || Number(item.Price) || 0;
          const subtotal =
            Number(item.subTotal) || Number(item.SubTotal) || price * quantity;
          const isGift = price === 0;

          return `
        <tr class="item-row">
          <td class="item-number">${index + 1}</td>
          <td class="item-name">
            ${itemName}
            ${isGift ? '<span class="gift-tag">贈品</span>' : ""}
          </td>
          <td class="item-qty">${quantity}</td>
          <td class="item-price">${
            isGift ? "免費" : "NT$ " + this.formatNumber(price)
          }</td>
          <td class="item-total">${
            isGift ? "贈送" : "NT$ " + this.formatNumber(subtotal)
          }</td>
        </tr>
        `;
        })
        .join("");

      // 計算統計資料
      const totalItems = order.items.reduce((sum, item) => {
        return sum + (Number(item.quantity) || Number(item.Quantity) || 0);
      }, 0);

      const giftItems = order.items.filter(
        (item) => (Number(item.price) || Number(item.Price) || 0) === 0,
      );

      const paidItems = order.items.filter(
        (item) => (Number(item.price) || Number(item.Price) || 0) > 0,
      );

      // 確保客戶和訂單資料
      const customerName =
        order.customerName || order.CompanyName || "客戶名稱缺失";
      const contactPerson =
        order.contactPerson ||
        order.CustomerName ||
        order.FullName ||
        "聯絡人缺失";
      const phone = order.phone || order.PhoneNumber || "電話號碼缺失";
      const address = order.address || order.Address || "地址缺失";
      const orderNumber = order.orderNumber || order.OrderNo || "訂單編號缺失";
      const orderDate =
        order.orderDate ||
        (order.CreatedDate ? order.CreatedDate.split("T")[0] : "日期缺失");
      const totalAmount =
        Number(order.totalAmount) || Number(order.TotalAmount) || 0;

      return `
<!DOCTYPE html>
<html lang="zh-TW">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>出貨單 - ${orderNumber}</title>
<style>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: "Microsoft JhengHei", "PingFang TC", Arial, sans-serif;
    line-height: 1.5;
    color: #2c3e50;
    background: #fff;
    padding: 20px;
  }

  .document {
    max-width: 800px;
    margin: 0 auto;
    background: white;
    box-shadow: 0 0 20px rgba(0,0,0,0.1);
  }

  /* 頁首設計 */
  .header {
    background: #34495e;
    color: white;
    padding: 30px;
    text-align: center;
    border-bottom: 4px solid #3498db;
  }

  .company-name {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 10px;
    letter-spacing: 2px;
  }

  .doc-title {
    font-size: 32px;
    font-weight: 300;
    letter-spacing: 6px;
    margin: 15px 0;
  }

  .doc-number {
    font-size: 16px;
    opacity: 0.9;
    background: rgba(255,255,255,0.1);
    padding: 8px 16px;
    border-radius: 20px;
    display: inline-block;
    margin-top: 10px;
  }

  /* 內容區域 */
  .content {
    padding: 30px;
  }

  /* 基本資訊區塊 */
  .info-section {
    margin-bottom: 30px;
    border: 1px solid #ecf0f1;
    border-radius: 8px;
    overflow: hidden;
  }

  .info-header {
    background: #3498db;
    color: white;
    padding: 12px 20px;
    font-weight: bold;
    font-size: 16px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
  }

  .info-item {
    padding: 15px 20px;
    border-right: 1px solid #ecf0f1;
    border-bottom: 1px solid #ecf0f1;
  }

  .info-item:nth-child(even) {
    border-right: none;
  }

  .info-label {
    font-weight: bold;
    color: #7f8c8d;
    font-size: 13px;
    display: block;
    margin-bottom: 5px;
  }

  .info-value {
    color: #2c3e50;
    font-size: 15px;
  }

  .status-badge {
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
  }

  .status-paid {
    background: #2ecc71;
    color: white;
  }

  .status-unpaid {
    background: #e74c3c;
    color: white;
  }

  /* 商品表格 */
  .products-section {
    margin: 30px 0;
    page-break-before: always; /* 強制在新頁開始 */
    break-before: page; /* CSS3 語法 */
  }

  .section-title {
    background: #34495e;
    color: white;
    padding: 15px 20px;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 0;
    border-radius: 8px 8px 0 0;
  }

  .products-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #34495e;
    border-radius: 0 0 8px 8px;
    overflow: hidden;
  }

  .products-table th {
    background: #ecf0f1;
    padding: 12px 10px;
    text-align: center;
    font-weight: bold;
    font-size: 14px;
    border-bottom: 2px solid #34495e;
  }

  .products-table td {
    padding: 12px 10px;
    border-bottom: 1px solid #ecf0f1;
    vertical-align: middle;
  }

  .item-row:hover {
    background: #f8f9fa;
  }

  .item-row:last-child td {
    border-bottom: none;
  }

  .item-number {
    text-align: center;
    font-weight: bold;
    color: #7f8c8d;
    width: 50px;
  }

  .item-name {
    font-weight: 500;
    position: relative;
  }

  .gift-tag {
    background: #f39c12;
    color: white;
    padding: 2px 6px;
    border-radius: 10px;
    font-size: 10px;
    margin-left: 8px;
    font-weight: bold;
  }

  .item-qty {
    text-align: center;
    font-weight: bold;
    color: #3498db;
    width: 80px;
  }

  .item-price {
    text-align: right;
    font-weight: 500;
    width: 120px;
  }

  .item-total {
    text-align: right;
    font-weight: bold;
    color: #27ae60;
    width: 120px;
  }

  /* 總計區塊 */
  .summary {
    margin-top: 20px;
    border: 2px solid #3498db;
    border-radius: 8px;
    overflow: hidden;
  }

  .summary-header {
    background: #3498db;
    color: white;
    padding: 12px 20px;
    font-weight: bold;
  }

  .summary-content {
    padding: 20px;
    background: #f8f9fa;
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    padding: 8px 0;
  }

  .summary-row:last-child {
    border-top: 2px solid #34495e;
    padding-top: 15px;
    margin-top: 15px;
    font-weight: bold;
    font-size: 18px;
  }

  .summary-label {
    color: #7f8c8d;
  }

  .summary-value {
    color: #2c3e50;
    font-weight: bold;
  }

  .grand-total {
    color: #e74c3c !important;
    font-size: 20px !important;
  }

  /* 備註區塊 */
  .notes {
    margin: 30px 0;
    background: #fff9e6;
    border: 1px solid #f1c40f;
    border-radius: 8px;
    padding: 20px;
  }

  .notes-title {
    color: #f39c12;
    font-weight: bold;
    margin-bottom: 10px;
    font-size: 16px;
  }

  .notes-content {
    color: #8e6a00;
    line-height: 1.6;
  }

  /* 頁尾 */
  .footer {
    margin-top: 40px;
    padding: 20px;
    background: #f8f9fa;
    border-top: 3px solid #34495e;
    text-align: center;
    border-radius: 0 0 8px 8px;
  }

  .footer-text {
    color: #7f8c8d;
    font-size: 14px;
    margin-bottom: 10px;
  }

  .print-date {
    color: #95a5a6;
    font-size: 12px;
    font-style: italic;
  }

  /* 打印樣式 */
  @media print {
    body {
      padding: 0;
    }
    
    .document {
      box-shadow: none;
      max-width: none;
    }
    
    .products-section {
      page-break-before: always; /* 商品明細強制在新頁開始 */
      page-break-inside: avoid; /* 避免表格被分割 */
    }
    
    .products-table {
      page-break-inside: avoid; /* 表格內容盡量不分頁 */
    }
    
    .info-section {
      page-break-inside: avoid; /* 資訊區塊不分頁 */
    }
    
    .summary {
      page-break-inside: avoid; /* 統計區塊不分頁 */
    }
  }
</style>
</head>

<body>
  <div class="document">
    
    <!-- 頁首 -->
    <div class="header">
      <div class="company-name">安裕和國際企業社</div>
      <div class="doc-title">出 貨 單</div>
      <div class="doc-number">單號：${orderNumber}</div>
    </div>

    <div class="content">
      
      <!-- 訂單基本資訊 -->
      <div class="info-section">
        <div class="info-header">📋 訂單資訊</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">訂單編號</span>
            <span class="info-value">${orderNumber}</span>
          </div>
          <div class="info-item">
            <span class="info-label">訂單日期</span>
            <span class="info-value">${orderDate}</span>
          </div>
        <div class="info-item">
  <span class="info-label">付款方式</span>
  <span class="info-value">${order.paymentMethod || "未提供"}</span>
</div>

          <div class="info-item">
            <span class="info-label">付款狀態</span>
            <span class="info-value">
              <span class="status-badge ${
                order.paymentStatus === "paid" ? "status-paid" : "status-unpaid"
              }">
                ${order.paymentStatus === "paid" ? "已付款" : "未付款"}
              </span>
            </span>
          </div>
        </div>
      </div>

      <!-- 客戶資訊 -->
      <div class="info-section">
        <div class="info-header">👤 客戶資訊</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">客戶名稱</span>
            <span class="info-value">${customerName}</span>
          </div>
          <div class="info-item">
            <span class="info-label">聯絡人</span>
            <span class="info-value">${contactPerson}</span>
          </div>
          <div class="info-item">
            <span class="info-label">聯絡電話</span>
            <span class="info-value">${phone}</span>
          </div>
          <div class="info-item">
            <span class="info-label">送貨地址</span>
            <span class="info-value">${address}</span>
          </div>
        </div>
      </div>

      <!-- 商品明細 -->
      <div class="products-section">
        <div class="section-title">
          📦 商品明細 (共 ${totalItems} 件商品${
        giftItems.length > 0 ? "，含 " + giftItems.length + " 項贈品" : ""
      })
        </div>
        
        <table class="products-table">
          <thead>
            <tr>
              <th>項次</th>
              <th>商品名稱</th>
              <th>數量</th>
              <th>單價</th>
              <th>小計</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>
      </div>

      <!-- 金額統計 -->
      <div class="summary">
        <div class="summary-header">💰 金額統計</div>
        <div class="summary-content">
          <div class="summary-row">
            <span class="summary-label">商品總數量：</span>
            <span class="summary-value">${totalItems} 件</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">付費商品：</span>
            <span class="summary-value">${paidItems.length} 項</span>
          </div>
          ${
            giftItems.length > 0
              ? `
          <div class="summary-row">
            <span class="summary-label">贈送商品：</span>
            <span class="summary-value">${giftItems.length} 項</span>
          </div>
          `
              : ""
          }
          <div class="summary-row">
            <span class="summary-label">訂單總金額：</span>
            <span class="summary-value grand-total">NT$ ${this.formatNumber(
              totalAmount,
            )}</span>
          </div>
        </div>
      </div>

      <!-- 備註 (如果有) -->
      ${
        order.note
          ? `
      <div class="notes">
        <div class="notes-title">📝 特別備註</div>
        <div class="notes-content">${order.note}</div>
      </div>
      `
          : ""
      }

    </div>

    <!-- 頁尾 -->
    <div class="footer">
      <div class="footer-text">感謝您的訂購，如有任何問題請隨時聯繫我們</div>
      <div class="print-date">列印時間：${new Date().toLocaleString(
        "zh-TW",
      )}</div>
    </div>

  </div>
</body>
</html>
`;
    },

    // ==============================================
    // ⭐ 錯誤處理PDF生成器
    // ==============================================
    generateErrorPDF(order, errorMessage) {
      return `
<!DOCTYPE html>
<html lang="zh-TW">
<head>
<meta charset="UTF-8">
<title>PDF 生成錯誤</title>
<style>
  body {
    font-family: "Microsoft JhengHei", Arial, sans-serif;
    padding: 50px;
    text-align: center;
    background: #f8f9fa;
  }
  
  .error-container {
    background: white;
    padding: 40px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    max-width: 600px;
    margin: 0 auto;
  }
  
  .error-icon {
    font-size: 48px;
    color: #e74c3c;
    margin-bottom: 20px;
  }
  
  .error-title {
    font-size: 24px;
    color: #2c3e50;
    margin-bottom: 15px;
  }
  
  .error-message {
    color: #7f8c8d;
    line-height: 1.6;
    margin-bottom: 30px;
  }
  
  .instructions {
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 4px;
    padding: 15px;
    color: #856404;
  }
</style>
</head>
<body>
  <div class="error-container">
    <div class="error-icon">⚠️</div>
    <h2 class="error-title">無法生成出貨單</h2>
    <p class="error-message">${errorMessage}</p>
    <div class="instructions">
      <strong>解決方法：</strong><br>
      1. 請先點擊「查看詳情」確認訂單資料已完整載入<br>
      2. 關閉詳情視窗後，再重新點擊「下載出貨單」<br>
      3. 如果問題持續，請聯絡系統管理員
    </div>
  </div>
</body>
</html>
`;
    },

    formatNumber(num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    // ⭐ 新增：切換付款狀態下拉
    togglePaymentDropdown(orderId) {
      if (this.activePaymentDropdown === orderId) {
        this.activePaymentDropdown = null;
      } else {
        this.activePaymentDropdown = orderId;
        this.activeStatusDropdown = null; // 關閉另一個
        this.activeDropdown = null;
      }
    },

    // ⭐ 新增：切換出貨狀態下拉
    toggleStatusDropdown(orderId) {
      if (this.activeStatusDropdown === orderId) {
        this.activeStatusDropdown = null;
      } else {
        this.activeStatusDropdown = orderId;
        this.activePaymentDropdown = null; // 關閉另一個
        this.activeDropdown = null;
      }
    },

    // ⭐ 新增:更新付款狀態 (需要串接後端 API)
    async updatePaymentStatus(order, newStatus) {
      const text = newStatus === "paid" ? "已付款" : "未付款";

      const confirmed = await showConfirm(
        `確定要將訂單「${order.orderNumber}」標記為 ${text} 嗎？`,
      );

      if (!confirmed.isConfirmed) return;

      showLoading("正在更新付款狀態...");

      try {
        const response = await api.put(`/order/update-status/${order.id}`, {
          orderStatus: order.status, // 保留現有狀態
          paymentStatus: newStatus === "paid", // 轉成 bool
        });

        const result = response.data;

        if (!result.success) {
          hideLoading();
          showCustom({
            icon: "error",
            title: "更新失敗",
            text: result.message ?? "後端回傳錯誤",
          });
          return;
        }

        // 🔥 成功後更新前端
        order.paymentStatus = newStatus;

        hideLoading();
        showSuccess(`付款狀態已更新為 ${text}！`);
      } catch (error) {
        hideLoading();
        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: "請稍後再試一次。",
        });
      }
    },
    // ⭐ 修改：updateOrderStatus 加入「已取消」狀態
    // ⭐ 更新訂單狀態（含已出貨不可逆提示）
    async updateOrderStatus(order, newStatus) {
      // ===============================
      // 控制區域：已出貨特殊警告
      // ===============================
      if (newStatus === "已出貨") {
        const shippedConfirm = await showConfirm(
          "此動作將標記出貨日期，且標記不可回復，確定要繼續嗎？",
        );

        if (!shippedConfirm.isConfirmed) return;
      } else {
        // 一般狀態正常確認
        const confirmed = await showConfirm(
          `確定要將訂單「${order.orderNumber}」設為 ${newStatus} 嗎?`,
        );

        if (!confirmed.isConfirmed) return;
      }

      showLoading("正在更新訂單狀態...");

      try {
        const response = await api.put(`/order/update-status/${order.id}`, {
          orderStatus: newStatus, // ← 傳送狀態
          paymentStatus: order.paymentStatus === "paid" ? true : false,
        });

        const result = response.data;

        if (!result.success) {
          hideLoading();

          showCustom({
            icon: "error",
            title: "更新失敗",
            text: result.message ?? "後端回傳錯誤",
          });

          return;
        }

        // ✅ 成功後更新前端畫面
        order.status = newStatus;

        hideLoading();

        // ===============================
        // 成功提示
        // ===============================
        if (newStatus === "已出貨") {
          showSuccess("訂單已標記為出貨完成，出貨日期已建立！");
        } else {
          showSuccess("訂單狀態已成功更新！");
        }
      } catch (error) {
        hideLoading();

        showCustom({
          icon: "error",
          title: "伺服器錯誤",
          text: "請稍後再試一次。",
        });
      }
    },
    // ⭐ 修改：getStatusClass 加入「已取消」樣式
    getStatusClass(status) {
      const classes = {
        待處理: "status-pending",
        處理中: "status-processing",
        已出貨: "status-shipped",
        已完成: "status-completed",
        已取消: "status-cancelled",
        申請退貨: "status-return", // 註解：新增的申請退貨狀態
      };
      return classes[status] || "";
    },

    // ⭐ 修改：getStatusText 加入「已取消」文字
    getStatusText(status) {
      const texts = {
        pending: "待處理",
        processing: "處理中",
        shipped: "已出貨",
        completed: "已完成",
        cancelled: "已取消", // ⭐ 新增
      };
      return texts[status] || status;
    },

    getPaymentMethodClass(method) {
      const classes = {
        轉帳: "pay-transfer",
        貨到付款: "pay-cod",
      };
      return classes[method] || "pay-default";
    },

    // ===================================================
    // ⭐ 匯出訂單數據（SW2 選單 + Excel）
    // 控制區域：匯出訂單
    // ===================================================
    async exportReport() {
      await showCustom({
        title: "匯出訂單資料",
        icon: null, // ⭐ 關掉 info 圖示
        showConfirmButton: false, // ⭐ 關掉「確定」
        showCancelButton: false, // ⭐ 保險
        allowOutsideClick: true, // ⭐ 可點外面關閉
        html: `
      <div style="display:flex;flex-direction:column;gap:12px">
        <button id="today" class="swal2-confirm swal2-styled">本日</button>
        <button id="week" class="swal2-confirm swal2-styled">本週</button>
        <button id="month" class="swal2-confirm swal2-styled">本月</button>
        <button id="custom" class="swal2-confirm swal2-styled">自訂日期</button>
      </div>
    `,
        didOpen: () => {
          document.getElementById("today").onclick = () =>
            this.exportByRange("today");
          document.getElementById("week").onclick = () =>
            this.exportByRange("week");
          document.getElementById("month").onclick = () =>
            this.exportByRange("month");
          document.getElementById("custom").onclick = () =>
            this.exportCustomRange();
        },
      });
    },
    // ===================================================
    // ⭐ 依固定區間匯出
    // 控制區域：日期判斷
    // ===================================================
    exportByRange(type) {
      const today = new Date();
      let start, end;

      // 📅 本日
      if (type === "today") {
        start = new Date(today.setHours(0, 0, 0, 0));
        end = new Date();
      }

      // 📅 本週
      if (type === "week") {
        const first = today.getDate() - today.getDay();
        start = new Date(today.setDate(first));
        start.setHours(0, 0, 0, 0);
        end = new Date();
      }

      // 📅 本月
      if (type === "month") {
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date();
      }

      this.generateExcel(start, end);
    },

    // ===================================================
    // ⭐ 自訂日期匯出
    // 控制區域：日期選擇
    // ===================================================
    async exportCustomRange() {
      const { value } = await showCustom({
        title: "選擇日期區間",
        html: `
      <div style="display: flex; align-items: center; gap: 10px; justify-content: center;">
        <input id="start" type="date" class="swal2-input" style="flex: 1; margin: 0;">
        <span style="font-size: 18px; font-weight: bold; color: #666;">~</span>
        <input id="end" type="date" class="swal2-input" style="flex: 1; margin: 0;">
      </div>
    `,
        didOpen: () => {
          const startInput = document.getElementById("start");
          const endInput = document.getElementById("end");

          // 🔄 更新結束日期的 min 屬性
          const updateEndDateMin = () => {
            if (startInput.value) {
              endInput.min = startInput.value;
            } else {
              endInput.min = "";
            }
          };

          // 🔄 更新起始日期的 max 屬性
          const updateStartDateMax = () => {
            if (endInput.value) {
              startInput.max = endInput.value;
            } else {
              startInput.max = "";
            }
          };

          // 📅 起始日期改變時更新結束日期的 min
          startInput.addEventListener("change", () => {
            updateEndDateMin();
            // 如果結束日期小於起始日期，清空結束日期
            if (endInput.value < startInput.value) {
              endInput.value = "";
            }
          });

          // 📅 結束日期改變時更新起始日期的 max
          endInput.addEventListener("change", () => {
            updateStartDateMax();
            // 如果起始日期大於結束日期，清空起始日期
            if (startInput.value > endInput.value) {
              startInput.value = "";
            }
          });
        },
        preConfirm: () => {
          const startValue = document.getElementById("start").value;
          const endValue = document.getElementById("end").value;

          // ✅ 驗證兩個日期都必須填寫
          if (!startValue || !endValue) {
            Swal.showValidationMessage("請選擇起始日期和結束日期");
            return false;
          }

          // ✅ 驗證結束日期不可小於起始日期
          if (endValue < startValue) {
            Swal.showValidationMessage("結束日期不可小於起始日期");
            return false;
          }

          return {
            start: startValue,
            end: endValue,
          };
        },
      });

      if (!value?.start || !value?.end) return;

      this.generateExcel(new Date(value.start), new Date(value.end));
    },

    // ===================================================
    // ⭐ 產生 Excel
    // 控制區域：Excel 匯出
    // ===================================================
    async generateExcel(startDate, endDate) {
      // 🔍 依日期過濾訂單
      const orders = this.orders.filter((o) => {
        const orderDate = new Date(o.orderDate);
        return orderDate >= startDate && orderDate <= endDate;
      });

      if (orders.length === 0) {
        // 關閉目前 SweetAlert (由 alertService 處理)
        hideLoading(); // ⭐ 保險關閉 loading

        await showCustom({
          icon: "info",
          title: "沒有訂單資料",
          text: "此區間沒有任何訂單紀錄",
        });

        return;
      }

      showLoading("正在整理 Excel 資料...");

      // ==============================
      // Sheet 1：訂單主檔
      // ==============================
      const orderSheet = orders.map((o) => ({
        訂單編號: o.orderNumber,
        客戶名稱: o.customerName,
        聯絡人: o.contactPerson,
        電話: o.phone,
        訂單狀態: o.status,
        付款狀態: this.getPaymentText(o.paymentStatus),
        訂單日期: o.orderDate,
        總金額: o.totalAmount,
      }));

      // ==============================
      // Sheet 2：商品明細（關鍵）
      // ==============================
      const productSheet = [];

      for (const order of orders) {
        // 🔥 呼叫「訂單詳細 API」
        const res = await api.get(`/order/detail/${order.id}`);
        if (!res.data.success) continue;

        const items = res.data.data.Items ?? [];

        items.forEach((item) => {
          productSheet.push({
            訂單編號: order.orderNumber,
            客戶名稱: order.customerName,
            商品名稱: item.VariantName
              ? `${item.ProductName} (${item.VariantName})`
              : item.ProductName,
            單價: item.Price,
            數量: item.Quantity,
            小計: item.SubTotal,
          });
        });
      }

      // ==============================
      // 建立 Excel
      // ==============================
      const workbook = XLSX.utils.book_new();

      const wsOrder = XLSX.utils.json_to_sheet(orderSheet);
      XLSX.utils.book_append_sheet(workbook, wsOrder, "訂單總表");

      const wsProduct = XLSX.utils.json_to_sheet(productSheet);
      XLSX.utils.book_append_sheet(workbook, wsProduct, "商品明細");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, `訂單匯出_${new Date().toISOString().slice(0, 10)}.xlsx`);

      hideLoading();
      showSuccess("匯出成功", "已包含商品明細");
    },
  },
};
